import React, { CSSProperties, ReactNode } from "react";
import Loader from "@/components/Loaders/LoaderCircle/Loader";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "danger"
  | "ghost"
  | "social";
export type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  loading?: boolean;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  onClick?: (event?: any) => any;
  className?: string;
  style?: CSSProperties;
  fullWidth?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-[#70B237] text-[#242C36] border border-[#477A1E] hover:bg-[#8FCA5C] hover:border-[#70B237] font-semibold",
  secondary:
    "bg-[#252238] text-white border border-[#312E47] hover:bg-[#312E47] hover:border-[#6D6D94]",
  danger:
    "bg-[#da4444] text-white border border-[#b33333] hover:bg-[#e55555] hover:border-[#da4444]",
  ghost:
    "bg-transparent text-white border border-[#312E47] hover:bg-[#252238] hover:border-[#6D6D94]",
  social:
    "bg-white text-[#242C36] border border-[#e0e0e0] hover:bg-[#f5f5f5] hover:border-[#c0c0c0] font-medium",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-xs gap-1.5",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2.5",
};

const iconSizes: Record<ButtonSize, number> = {
  sm: 14,
  md: 16,
  lg: 18,
};

const Button = ({
  children,
  variant = "primary",
  size = "md",
  icon,
  iconPosition = "left",
  loading = false,
  disabled = false,
  type = "button",
  onClick,
  className = "",
  style,
  fullWidth = false,
}: ButtonProps) => {
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      style={style}
      className={[
        "inline-flex items-center justify-center rounded-lg font-poppins transition-all duration-150 cursor-pointer",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variantClasses[variant],
        sizeClasses[size],
        fullWidth ? "w-full" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {loading ? (
        <Loader isLoading={true} width={iconSizes[size]} height={iconSizes[size]} color="white" />
      ) : (
        <>
          {icon && iconPosition === "left" && icon}
          {children}
          {icon && iconPosition === "right" && icon}
        </>
      )}
    </button>
  );
};

export default Button;
